import { useState } from 'react';

import { DropdownMenu } from '../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { translate } from '../../../common/helpers/translate.helper';
import { IconDots } from '../../../common/icons/cdl/Dots';
import { useDeleteVesselProducts } from '../hooks/useDeleteVesselProducts';

import { RemoveAllProductsModal } from './RemoveAllProductsModal';
import { useVesselProductsDownload } from './useVesselProductsDownload';

interface VesselProductsActionsDropdownProps {
    vesselId: string;
    onUploadClick: () => void;
    productIds: string[];
}

export const VesselProductsActionsDropdown = ({
    vesselId,
    onUploadClick,
    productIds,
}: VesselProductsActionsDropdownProps) => {
    const downloadVesselProducts = useVesselProductsDownload();
    const deleteProductsMutation = useDeleteVesselProducts(vesselId);
    const { addToast, addErrorToast } = useToasts();
    const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);

    const handleExport = () => {
        downloadVesselProducts(vesselId);
    };

    const handleRemoveAll = async () => {
        try {
            await deleteProductsMutation.mutateAsync(productIds);
            setShowRemoveAllModal(false);
            addToast(translate('vessel.products.toasts.deleteAllSuccess'));
        } catch {
            setShowRemoveAllModal(false);
            addErrorToast(translate('vessel.products.toasts.deleteAllError'));
        }
    };

    return (
        <>
            <DropdownMenu
                align="end"
                trigger={<IconButton emphasis="medium" Icon={IconDots} />}
                options={[
                    {
                        label: translate('vessel.products.uploadButton'),
                        onSelect: onUploadClick,
                    },
                    {
                        label: translate('vessel.products.export'),
                        onSelect: handleExport,
                    },
                    {
                        label: translate('vessel.products.actions.removeAll'),
                        destructive: true,
                        onSelect: () => setShowRemoveAllModal(true),
                    },
                ]}
            />
            <RemoveAllProductsModal
                isOpen={showRemoveAllModal}
                onDismiss={() => setShowRemoveAllModal(false)}
                onSubmit={handleRemoveAll}
                isSubmitting={deleteProductsMutation.isPending}
            />
        </>
    );
};
